import { useCallback, useEffect, useRef, useState } from 'react';
import { useClientCountry } from '../hooks/useClientCountry';
import {
  fetchPublicConsultants,
  fetchPublicHomeCarousel,
  fetchPublicHomeSections,
  fetchPublicStoreProducts,
  type PublicHomeCarouselSlide,
  mapPublicProductToService,
  mapPublicSectionToHomeConfig,
  type PublicStoreProduct,
} from '../services/appContentService';
import { resolveHomeItemImage, resolveStoreProductImage } from '../services/appContentImageFallback';
import { normalizeTakerForBooking } from '../features/booking/services/appointmentService';
import { HOME_TOPIC_SECTIONS } from '../features/marketplace/homeTopicSections';
import type { HomeTopicSectionConfig } from '../features/marketplace/homeTopicSections';
import type { Service } from '../features/marketplace/components/ServiceCard';
import type { AppointmentTaker } from '../features/marketplace/types';
import svcFirearm from '../../assets/shooting-range-safety-officer-demonstrating-how-safely-reload-firearms.jpg';
import svcTax from '../../assets/pexels-n-voitkevich-6863191.jpg';

const FALLBACK_STORE: Service[] = [
  {
    slug: 'canadian-firearm-course',
    title: 'Canadian Firearm Course',
    badge: 'High demand',
    badgeType: 'demand',
    category: 'Licensing',
    price: 'CAD $565.00',
    image: svcFirearm,
    placeholderColor: '#1a3a5c',
    shortDesc: 'CFSC/CRFSC certification class with full exam preparation.',
    fullDesc: 'CFSC/CRFSC certification class with full exam preparation.',
    highlights: ['Certificate valid across all Canadian provinces'],
  },
  {
    slug: 'individual-tax-filing',
    title: 'Individual Tax Filing',
    category: 'Financial',
    price: 'CAD $56.50',
    image: svcTax,
    placeholderColor: '#1a5c3a',
    shortDesc: 'Personal income tax return preparation and CRA filing.',
    fullDesc: 'Personal income tax return preparation and CRA filing.',
    highlights: ['CRA e-filing included'],
  },
];

function dedupeBySlug<T extends { slug: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = item.slug.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** How often to refresh CMS content while the marketplace tab is visible. */
const HOME_CONTENT_POLL_MS = 12_000;

type LoadOptions = { silent?: boolean };

function dedupeHomeSections(sections: HomeTopicSectionConfig[]): HomeTopicSectionConfig[] {
  const seen = new Set<string>();
  return sections
    .filter((section) => {
      const key = section.id.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((section) => ({
      ...section,
      items: dedupeBySlug(section.items),
    }));
}

export function useAppHomeContent() {
  const { country } = useClientCountry();
  const countryRef = useRef(country);
  countryRef.current = country;
  const sectionsRequestIdRef = useRef(0);

  const [sections, setSections] = useState<HomeTopicSectionConfig[]>(() =>
    country === 'CA' ? HOME_TOPIC_SECTIONS : []
  );
  const [storeServices, setStoreServices] = useState<Service[]>(() =>
    country === 'CA' ? FALLBACK_STORE : []
  );
  const [takers, setTakers] = useState<AppointmentTaker[]>([]);
  const [storeLoading, setStoreLoading] = useState(true);
  const [sectionsLoading, setSectionsLoading] = useState(true);
  const [consultantsLoading, setConsultantsLoading] = useState(true);
  const [carouselSlides, setCarouselSlides] = useState<PublicHomeCarouselSlide[]>([]);
  const [carouselLoading, setCarouselLoading] = useState(true);

  const loadStore = useCallback(async (options?: LoadOptions) => {
    const silent = options?.silent ?? false;
    const requestCountry = country;
    if (!silent) setStoreLoading(true);
    try {
      let products: PublicStoreProduct[] | null = null;
      try {
        products = await fetchPublicStoreProducts(requestCountry);
      } catch {
        products = null;
      }
      if (countryRef.current !== requestCountry) return;

      const mapped =
        products !== null
          ? products.map((p) => mapPublicProductToService(p, resolveStoreProductImage(p.slug, p.image_url)))
          : requestCountry === 'CA'
            ? FALLBACK_STORE
            : [];

      setStoreServices(dedupeBySlug(mapped));
    } finally {
      if (!silent && countryRef.current === requestCountry) {
        setStoreLoading(false);
      }
    }
  }, [country]);

  const loadSections = useCallback(async (options?: LoadOptions) => {
    const silent = options?.silent ?? false;
    const requestCountry = country;
    const requestId = ++sectionsRequestIdRef.current;
    if (!silent) setSectionsLoading(true);
    try {
      let homeSections: Awaited<ReturnType<typeof fetchPublicHomeSections>> | null = null;
      try {
        homeSections = await fetchPublicHomeSections(requestCountry);
      } catch {
        homeSections = null;
      }
      if (requestId !== sectionsRequestIdRef.current || countryRef.current !== requestCountry) return;

      const mapped =
        homeSections !== null
          ? homeSections.map((section) => mapPublicSectionToHomeConfig(section, resolveHomeItemImage))
          : requestCountry === 'CA'
            ? HOME_TOPIC_SECTIONS
            : [];

      setSections(dedupeHomeSections(mapped));
    } finally {
      if (!silent && requestId === sectionsRequestIdRef.current) {
        setSectionsLoading(false);
      }
    }
  }, [country]);

  const loadCarousel = useCallback(async (options?: LoadOptions) => {
    const silent = options?.silent ?? false;
    const requestCountry = country;
    if (!silent) setCarouselLoading(true);
    try {
      const slides = await fetchPublicHomeCarousel(requestCountry).catch(() => [] as PublicHomeCarouselSlide[]);
      if (countryRef.current !== requestCountry) return;
      setCarouselSlides(slides);
    } finally {
      if (!silent && countryRef.current === requestCountry) {
        setCarouselLoading(false);
      }
    }
  }, [country]);

  const loadConsultants = useCallback(async (options?: LoadOptions) => {
    const silent = options?.silent ?? false;
    const requestCountry = country;
    if (!silent) setConsultantsLoading(true);
    try {
      const consultants = await fetchPublicConsultants(requestCountry).catch(() => [] as AppointmentTaker[]);
      if (countryRef.current !== requestCountry) return;

      setTakers(
        consultants.map((taker) => normalizeTakerForBooking(taker, taker.slug ?? ''))
      );
    } finally {
      if (!silent && countryRef.current === requestCountry) {
        setConsultantsLoading(false);
      }
    }
  }, [country]);

  const reload = useCallback(async () => {
    await Promise.all([loadStore(), loadSections(), loadConsultants(), loadCarousel()]);
  }, [loadStore, loadSections, loadConsultants, loadCarousel]);

  useEffect(() => {
    void loadStore();
  }, [loadStore]);

  useEffect(() => {
    void loadSections();
  }, [loadSections]);

  useEffect(() => {
    void loadConsultants();
  }, [loadConsultants]);

  useEffect(() => {
    void loadCarousel();
  }, [loadCarousel]);

  useEffect(() => {
    const refreshVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void loadSections({ silent: true });
      void loadStore({ silent: true });
      void loadConsultants({ silent: true });
      void loadCarousel({ silent: true });
    };

    const intervalId = window.setInterval(refreshVisible, HOME_CONTENT_POLL_MS);
    window.addEventListener('focus', refreshVisible);
    document.addEventListener('visibilitychange', refreshVisible);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshVisible);
      document.removeEventListener('visibilitychange', refreshVisible);
    };
  }, [loadSections, loadStore, loadConsultants, loadCarousel]);

  return {
    country,
    sections,
    storeServices,
    takers,
    carouselSlides,
    storeLoading,
    sectionsLoading,
    consultantsLoading,
    carouselLoading,
    loading: storeLoading || sectionsLoading || consultantsLoading || carouselLoading,
    reload,
  };
}
